// safety/safety-walks/components/EditSafetyWalkDrawer.tsx
// Slide-over for editing a safety walk's Overview information. Reuses the
// same field set as the New Safety Walk page (WalkFormFields); the parent
// page owns the save (PATCH /api/safety/walks/:id).
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Drawer } from './shared';
import { WalkFormFields } from './WalkFormFields';
import type { WalkFormValue, SelectionUser, ProjectRef } from './WalkFormFields';
import type { SafetyWalk } from '../../../../types/safety-walk.types';

// Only the Overview (walk-information) fields are editable here — walkNumber,
// status and the audit trail are server-managed and never sent back.
const pickEditable = (w: SafetyWalk, users: SelectionUser[]): WalkFormValue => {
    // Conductor combobox value is a user id; a stored conductor who is no
    // longer in the system is preserved as a `name:<name>` snapshot so the
    // editor never silently drops what was saved.
    let conductedBy = '';
    if (w.conductedById !== null && w.conductedById !== undefined) {
        const byId = users.find((u) => u.id === w.conductedById);
        if (byId) conductedBy = String(byId.id);
    }
    if (!conductedBy && w.conductedBy) {
        const byName = users.find((u) => u.name === w.conductedBy);
        conductedBy = byName ? String(byName.id) : `name:${w.conductedBy}`;
    }

    return {
        project: w.project || '',
        location: w.location || '',
        walkDate: w.walkDate ? w.walkDate.slice(0, 10) : '',
        conductedBy,
        participants: (w.participants || []).map((p) => ({ id: p.id, name: p.name })),
        generalComments: w.generalComments || '',
    };
};

export const EditSafetyWalkDrawer: React.FC<{
    open: boolean;
    walk: SafetyWalk;
    projects: ProjectRef[];
    users: SelectionUser[];
    onClose: () => void;
    onSave: (values: WalkFormValue) => Promise<void>;
}> = ({ open, walk, projects, users, onClose, onSave }) => {
    const [d, setD] = useState<WalkFormValue>(() => pickEditable(walk, users));
    const [submitting, setSubmitting] = useState(false);
    const set = (patch: Partial<WalkFormValue>) => setD((prev) => ({ ...prev, ...patch }));

    // Reset the form to the walk's current values each time the drawer opens
    useEffect(() => {
        if (open) setD(pickEditable(walk, users));
    }, [open, walk, users]);

    const submit = async () => {
        setSubmitting(true);
        try {
            await onSave(d);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer open={open} onClose={onClose} width="640px">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div>
                    <h3 className="text-base font-semibold">Edit Safety Walk</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{walk.walkNumber}</p>
                </div>
                <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                    <X size={20} />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                <WalkFormFields value={d} onChange={set} projects={projects} users={users} />
            </div>
            <div className="px-5 py-3.5 border-t border-border flex justify-end gap-2">
                <button
                    onClick={onClose}
                    disabled={submitting}
                    className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                >
                    Cancel
                </button>
                <button
                    onClick={submit}
                    disabled={submitting}
                    className="h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {submitting ? 'Saving…' : 'Save Changes'}
                </button>
            </div>
        </Drawer>
    );
};

export default EditSafetyWalkDrawer;
